/**
 * Client-pixel <-> normalised-frame maths for the detection-area editor.
 *
 * The video element is `object-fit: contain` (App.css, `.video-stream`), so the picture
 * almost never fills its box: it is letterboxed or pillarboxed, and the bars are dead space
 * that belongs to no pixel of the frame. Every coordinate the operator produces by clicking
 * therefore has to be mapped through the *content* rect, not the element rect — otherwise an
 * area drawn on a 16:9 feed inside a 4:3 panel lands somewhere else entirely once the backend
 * multiplies it by the real frame size.
 *
 * Geometry is stored normalised (0..1), the same convention as `tripwires.line_px` and DCIM
 * photo annotations, so it survives the panel being resized, popped out, or the camera
 * changing resolution.
 */

export interface ContentRect {
  /** Offset of the picture inside the element, in element-relative pixels. */
  left: number;
  top: number;
  /** Size of the picture itself — never larger than the element. */
  width: number;
  height: number;
}

export type NormPoint = [number, number];

/** Where the picture actually sits inside an `object-fit: contain` element. */
export function contentRect(
  elementWidth: number,
  elementHeight: number,
  naturalWidth: number,
  naturalHeight: number,
): ContentRect {
  if (elementWidth <= 0 || elementHeight <= 0 || naturalWidth <= 0 || naturalHeight <= 0) {
    return { left: 0, top: 0, width: 0, height: 0 };
  }
  const scale = Math.min(elementWidth / naturalWidth, elementHeight / naturalHeight);
  const width = naturalWidth * scale;
  const height = naturalHeight * scale;
  return {
    left: (elementWidth - width) / 2,
    top: (elementHeight - height) / 2,
    width,
    height,
  };
}

const clamp01 = (v: number) => (v < 0 ? 0 : v > 1 ? 1 : v);

/**
 * Element-relative pixel -> normalised frame coordinate.
 * Clamped, so a click that lands on a letterbox bar snaps to the nearest edge of the
 * picture rather than producing an out-of-range point the backend would reject.
 */
export function toNormalized(x: number, y: number, rect: ContentRect): NormPoint {
  if (rect.width <= 0 || rect.height <= 0) return [0, 0];
  return [clamp01((x - rect.left) / rect.width), clamp01((y - rect.top) / rect.height)];
}

/** Normalised frame coordinate -> element-relative pixel. */
export function toClient(nx: number, ny: number, rect: ContentRect): [number, number] {
  return [rect.left + nx * rect.width, rect.top + ny * rect.height];
}

/** Pixel distance between a normalised point and an element-relative pixel. */
export function distanceToPoint(
  point: NormPoint,
  x: number,
  y: number,
  rect: ContentRect,
): number {
  const [px, py] = toClient(point[0], point[1], rect);
  return Math.hypot(px - x, py - y);
}

/** How close to the first vertex a click has to land to close the polygon. */
export const CLOSE_THRESHOLD_PX = 14;

/** Minimum vertices for a usable area. */
export const MIN_POINTS = 3;

/** Mirrors detection_roi.MAX_POINTS — the backend rejects anything longer. */
export const MAX_POINTS = 64;

/** An SVG `points` attribute for a normalised polygon. */
export function toSvgPoints(points: NormPoint[], rect: ContentRect): string {
  return points
    .map(([nx, ny]) => {
      const [x, y] = toClient(nx, ny, rect);
      return `${x},${y}`;
    })
    .join(' ');
}
